import type { Dispatch, SetStateAction } from 'react';

import type {
	NotifyMode,
	GeneralAccessLevel,
	UserPickerPrincipal,
	PrincipalAndRole,
	Role,
	StagedPrincipalsAddMode,
	RestrictedUserPrincipal,
	AssignableSpaceRole,
} from '../controlled/commonTypes';

export type MasterStateSetter = Dispatch<SetStateAction<MasterState>>;

/** Null denotes "shareAndRestrict" */
export type DialogVariant = 'publicLink' | 'shareToSlack' | 'accessRequests' | null;

/**
 * The staged change for the General Access level: either unchanged, or a new
 * level
 */
export type GeneralAccessDelta = 'none' | GeneralAccessLevel;

/**
 * This is a single structure that encapsulates all of the staged changes that
 * impact the Specific Action section.
 *
 * It is designed to keep staged changes and incumbent query data entirely
 * separated from each other. This avoids the potential for conflicting sources
 * of truth, and sets us up for two potential improvements in the future:
 *
 * 1. a deltas-oriented mutation, through which we wouldn't send a complete list
 *    of all content permissions to replace the current one, but a succinct set
 *    of changes. This will aid with scaling, and prevent harm from race
 *    conditions of conflicting changes.
 * 2. a subscription-based view, in which third-party changes made to content
 *    restrictions while the dialog is open could be reflected in the view.
 *
 * However, its primary design concern is facilitating dialog rendering. Thus,
 * for example, order matters in the `additions` list -- a concern that wouldn't
 * be relevant for a mutation.
 */
export type SpecificAccessDelta = {
	/** Did the 'Remove all' button get clicked? */
	removeAll: boolean;

	/**
	 * Principals selected in the user picker, ready to be added or updated
	 * in the Specific Access list in the next session.
	 * Changes take effect after confirming the CTA.
	 */
	stagedAdditions: PrincipalAndRole[];

	/** principals removed from Specific Access during this session */
	removals: Set<string>; // id's

	/**
	 * principals who were already in Specific Access, from the source query, but
	 * are having their roles reassigned
	 */
	reassignments: Map<string, Role>;
};

/**
 * The authoritative state of the Share dialog stack. Each field must be a SSOT:
 * avoid interdependencies with other fields, query data and props.
 */
export type MasterState = {
	/**
	 * Which of the mutually-exclusive dialogs, collectively comprising the Share
	 * workflow, is open right now?
	 */
	sessionState: /**
	 * The main Share dialog is open, even though it may not be in the default
	 * 'mode' (in which case it'll have a different title, and a back-button to
	 * the left of it)
	 *
	 * The available modes are defined by the DialogVariant type
	 */
	| 'shareAndRestrict'
		/**
		 * There are users with restricted access
		 */
		| 'warnRestrictedAccess'
		/**
		 * The user has committed to a sharing action, and is getting friction on
		 * whether they really want to send notifications to all group members
		 */
		| 'confirmGroupNotifications'
		/**
		 * The user has completed a sharing action, and is now considering
		 * whether to try take action to unblock newly-added principals
		 */
		| 'resolveRestrictions'
		/**
		 * The session has completed. None of the above dialogs should be shown,
		 * and we'll rely on the teardown controller ending the session after
		 * whichever one was open closes.
		 */
		| 'exiting';

	/** The modal is open for confirming the discard of unsaved changes */
	isCloseConfirmationOpen: boolean;

	/** The users, groups and emails staged in the user picker */
	principalsStagerPrincipals: UserPickerPrincipal[];

	/** The user's most recent choice for what to do with staged principals */
	addMode: StagedPrincipalsAddMode;

	/**
	 * The add mode for each individual user in the Access Requests Dialog. Used by Resolve Restrictions Dialog overlay in Access Requests.
	 * Choosing to add as a separate piece of state to the addMode above, as that one is tightly coupled with share and restrict dialog
	 * */
	accessRequestsCurrentUserAddMode: StagedPrincipalsAddMode | undefined;

	/**
	 * User picker should have a warning that the dialog's CTA can't take effect
	 * while the user picker has un-added principals present.
	 */
	hasAddWarning: boolean;

	/** What change, if any, has been applied to the General Access level */
	generalAccessDelta: GeneralAccessDelta;

	/**
	 * Controls if the general access menu select should be controlled.
	 * In most cases, the menu will manage itself, but in certain scenarios, we need to force it to close.
	 */
	shouldGeneralAccessSelectMenuBeControlled: boolean;

	/**
	 * The changes, if any, that have been applied to the Specific Access control
	 */
	specificAccessDelta: SpecificAccessDelta;

	/** The space access details dialog is open */
	isSpaceAccessDetailsOpen: boolean;

	/** The inspect perms dialog is open */
	isInspectPermsDialogOpen: boolean;

	/** The Open access dialog is open */
	isOpenAccessWarningDialogOpen: boolean;

	/**
	 * The Public link dialog is open
	 */
	isPublicLinkDialogOpen: boolean;

	/**
	 * The Share to Slack dialog is open
	 */
	isShareToSlackDialogOpen: boolean;

	/**
	 * The specific access dialog is open
	 */
	isSpecificAccessDialogOpen: boolean;

	/**
	 * The Access Requests dialog is open
	 */
	isAccessRequestsDialogOpen: boolean;

	/**
	 * Access Requests Dialog may trigger the Resolve Restrictions Dialog as an overlay
	 * */
	isAccessRequestsResolveRestrictionsDialogOpen: boolean;

	/**
	 * Access Requests Dialog may trigger the Wan Restrictions Dialog as an overlay
	 * */
	isAccessRequestsWarnRestrictionsDialogOpen: boolean;

	/** If the notify checkbox is present, should it be checked or not? */
	notifyCheckedIfAvailable: boolean;

	/** Send dialog's notify mode */
	notifyMode: NotifyMode;

	/** Should the notify-new-users checkbox be expanded to show the composer? */
	showMessageComposerUnderCheckbox: boolean;

	/** Staged text for notification messages */
	optionalMessage: string;

	/** The main mutation is underway */
	isSaving: boolean;

	/** newly added principals that are restricted */
	restrictedUserPrincipals: RestrictedUserPrincipal[] | undefined;

	/** space roles that can be assigned to resolve possible space restrictions above */
	assignableSpaceRolesIntersection: AssignableSpaceRole[] | undefined;

	/**
	 * Determines whether the Specific Access list is expanded or collapsed
	 */
	isSpecificAccessExpanded: boolean;

	/**
	 * The last dialog variant that was triggered so that keyboard focus can be set on the correct element
	 */
	lastTriggeredDialogVariant: DialogVariant;

	/**
	 * Indicates whether mismatched restrictions should be resolved when a user selects
	 * "Open" access. This specifically handles the case where a group which holds restrictions on the content has been deleted,
	 * and is the only remaining restriction on said content.
	 * (CONFCLOUD-80781 https://jira.atlassian.com/browse/CONFCLOUD-80781).
	 *
	 * When true, any outdated or orphaned restrictions (such as those from deleted groups)
	 * will be cleared to ensure the page's access settings are correctly updated.
	 */
	shouldResolveMismatchedRestrictions: boolean;

	/**
	 * Indicates whether there are unsaved changes in the Access Requests dialog.
	 */
	hasAccessRequestsChanges: boolean;

	/** The current filter/search text for filtering principals in the specific access dialog. */
	specificAccessFilterText: string;

	/** tab selection index */
	tabSelection: number;
};

export function makeDefaultMasterState(initialDialog: DialogVariant = null): MasterState {
	return {
		sessionState: 'shareAndRestrict',
		isCloseConfirmationOpen: false,
		principalsStagerPrincipals: [],
		addMode: 'as-editor',
		accessRequestsCurrentUserAddMode: undefined,
		hasAddWarning: false,
		generalAccessDelta: 'none',
		shouldGeneralAccessSelectMenuBeControlled: false,
		specificAccessDelta: {
			removeAll: false,
			stagedAdditions: [],
			removals: new Set(),
			reassignments: new Map(),
		},
		isSpaceAccessDetailsOpen: false,
		isInspectPermsDialogOpen: false,
		isPublicLinkDialogOpen: false,
		isShareToSlackDialogOpen: false,
		isSpecificAccessDialogOpen: false,
		isOpenAccessWarningDialogOpen: false,
		isAccessRequestsDialogOpen: initialDialog === 'accessRequests',
		isAccessRequestsResolveRestrictionsDialogOpen: false,
		isAccessRequestsWarnRestrictionsDialogOpen: false,
		notifyCheckedIfAvailable: true,
		notifyMode: 'notify',
		optionalMessage: '',
		showMessageComposerUnderCheckbox: false,
		isSaving: false,
		restrictedUserPrincipals: undefined,
		assignableSpaceRolesIntersection: undefined,
		isSpecificAccessExpanded: false,
		lastTriggeredDialogVariant: null,
		shouldResolveMismatchedRestrictions: false,
		hasAccessRequestsChanges: false,
		specificAccessFilterText: '',
		tabSelection: 0,
	};
}
